import {
  BREAKPOINTS,
  FONT,
  GAEA_COLOURS,
  PALETTE_LIGHT,
  SHADOWS,
  SPACING,
  mediaMinWidth,
  styled,
} from "@dexibit/styles";

import {
  MOBILE_BAR_HEIGHT,
  NAV_ITEM_HEIGHT,
  NAV_SUB_ITEM_HEIGHT,
  NAV_TRANSITION,
  NAV_WIDTH_COLLAPSED,
  NAV_WIDTH_EXPANDED,
} from "./constants";

export const StyledNav = styled("nav")`
  ${mediaMinWidth.tablet(`
    width: ${NAV_WIDTH_COLLAPSED}px;
    height: 100%;
    position: relative;
  `)}
`;

export const SidebarContent = styled("div", {
  shouldForwardProp: (prop) => prop !== "isExpanded" && prop !== "isMobileOpen",
})<{ isExpanded: boolean; isMobileOpen: boolean }>(
  ({ isExpanded, isMobileOpen }) => `
  position: fixed;
  top: ${MOBILE_BAR_HEIGHT}px;
  left: ${isMobileOpen ? "0" : "-100vw"};
  bottom: 0;
  width: 100vw;
  z-index: 100;
  background: ${PALETTE_LIGHT.SURFACE_BASE};
  border-right: 1px solid ${PALETTE_LIGHT.SURFACE_BORDER};
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  visibility: ${isMobileOpen ? "visible" : "hidden"};
  transition: left 220ms ease, visibility 0s ${isMobileOpen ? "0s" : "220ms"};

  @media (min-width: ${BREAKPOINTS.tablet}px){
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: ${isExpanded ? NAV_WIDTH_EXPANDED : NAV_WIDTH_COLLAPSED}px;
    transition: width ${NAV_TRANSITION};
    visibility: visible;
    z-index: 50;
    overflow-y: auto;
    overflow-x: hidden;
    ${isExpanded ? `box-shadow: ${SHADOWS.boxShadowLg};` : ""}
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`,
);

export const LogoContainer = styled("div")`
  height: ${NAV_ITEM_HEIGHT}px;
  display: flex;
  /* Top-aligned, not centred: the wordmark is slightly taller than the mark, so
     centring each child independently left their top edges 1px apart. */
  align-items: flex-start;
  flex-shrink: 0;
  overflow: hidden;
  border-left: 3px solid transparent;
  padding-top: ${SPACING.regular};
  /* Breathing room above the logo. Margin rather than more padding-top, which
     would eat into the content box the 28px wordmark already nearly fills. */
  margin-top: ${SPACING.xsmall};
`;

export const LogoIconArea = styled("div")`
  width: 53px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const HomeItemWrapper = styled("div")`
  padding: ${SPACING.xlarge} 0;
`;

export const NavGroup = styled("div")`
  padding: ${SPACING.regular} 0;
`;

export const NavDivider = styled("div")`
  height: 1px;
  background: ${PALETTE_LIGHT.SURFACE_BORDER};
  margin: 0 ${SPACING.large};
`;

export const NavItemRow = styled("button", {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive: boolean }>(
  ({ isActive }) => `
  appearance: none;
  border: none;
  font: inherit;
  color: inherit;
  text-align: left;
  width: 100%;
  padding: 0;
  height: ${NAV_ITEM_HEIGHT}px;
  display: flex;
  align-items: center;
  cursor: pointer;
  border-left: 3px solid ${isActive ? PALETTE_LIGHT.PRIMARY_BASE : "transparent"};
  background: ${isActive ? PALETTE_LIGHT.PRIMARY_CONTRAST_LOWEST : "transparent"};
  color: ${isActive ? PALETTE_LIGHT.PRIMARY_BASE : PALETTE_LIGHT.TEXT_EMPHASIS_HIGH};
  user-select: none;

  &:hover {
    background: ${isActive ? PALETTE_LIGHT.PRIMARY_CONTRAST_LOWEST : PALETTE_LIGHT.SURFACE_TEXTURE};
  }
`,
);

export const NavItemIcon = styled("div")`
  width: 53px;
  height: ${NAV_ITEM_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const NavItemLabel = styled("span", {
  shouldForwardProp: (prop) => prop !== "isExpanded",
})<{ isExpanded: boolean }>(
  ({ isExpanded }) => `
  font-size: ${FONT.BODY_REGULAR.fontSize};
  white-space: nowrap;
  flex: 1;
  overflow: hidden;

  @media (min-width: ${BREAKPOINTS.tablet}px) {
    opacity: ${isExpanded ? 1 : 0};
    transition: opacity ${NAV_TRANSITION};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`,
);

export const AccordionToggle = styled("span", {
  shouldForwardProp: (prop) => prop !== "isOpen" && prop !== "isExpanded",
})<{ isOpen: boolean; isExpanded: boolean }>(
  ({ isOpen, isExpanded }) => `
  padding: 0 ${SPACING.regular};
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  height: 100%;
  transform: rotate(${isOpen ? "90deg" : "0deg"});
  transition: transform 200ms ease;
  pointer-events: none;

  @media (min-width: ${BREAKPOINTS.tablet}px) {
    opacity: ${isExpanded ? 1 : 0};
    transition: transform 200ms ease, opacity ${NAV_TRANSITION};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`,
);

export const SubItemList = styled("div", {
  shouldForwardProp: (prop) =>
    prop !== "isOpen" && prop !== "itemCount" && prop !== "isExpanded",
})<{ isOpen: boolean; itemCount: number; isExpanded: boolean }>(
  ({ isOpen, itemCount, isExpanded }) => `
  overflow: hidden;
  transition: max-height 250ms ease;
  max-height: ${isOpen ? itemCount * NAV_SUB_ITEM_HEIGHT : 0}px;

  @media (min-width: ${BREAKPOINTS.tablet}px) {
    ${!isExpanded ? "max-height: 0; transition: none;" : ""}
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`,
);

export const SubItemRow = styled("button", {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive: boolean }>(
  ({ isActive }) => `
  appearance: none;
  border: none;
  font: inherit;
  color: inherit;
  text-align: left;
  width: 100%;
  padding: 0;
  height: ${NAV_SUB_ITEM_HEIGHT}px;
  padding-left: 57px;
  font-size: ${FONT.BODY_SMALL.fontSize};
  display: flex;
  align-items: center;
  cursor: pointer;
  border-left: 3px solid ${isActive ? PALETTE_LIGHT.PRIMARY_BASE : "transparent"};
  background: ${isActive ? PALETTE_LIGHT.PRIMARY_CONTRAST_LOWEST : "transparent"};
  color: ${isActive ? PALETTE_LIGHT.PRIMARY_BASE : PALETTE_LIGHT.TEXT_EMPHASIS_HIGH};

  &:hover {
    background: ${isActive ? PALETTE_LIGHT.PRIMARY_CONTRAST_LOWEST : PALETTE_LIGHT.SURFACE_TEXTURE};
  }
`,
);

export const MessengerDecoration = styled("span", {
  shouldForwardProp: (prop) => prop !== "isExpanded",
})<{ isExpanded: boolean }>(
  ({ isExpanded }) => `
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${SPACING.regular};
  color: inherit;
  position: relative;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: ${PALETTE_LIGHT.SURFACE_TEXTURE};
  }

  @media (min-width: ${BREAKPOINTS.tablet}px){
    opacity: ${isExpanded ? 1 : 0};
    transition: opacity ${NAV_TRANSITION};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`,
);

export const NavSpacer = styled("div")`
  flex: 1;
`;

export const UnreadBadge = styled("span")`
  position: absolute;
  top: 6px;
  right: 6px;
  border-radius: 50%;
  background-color: ${GAEA_COLOURS.UTILITY.RED.RED};
  color: white;
  min-width: 16px;
  height: 16px;
  font-size: 11px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 4px;
`;

export const FooterContainer = styled("div")`
  height: ${NAV_WIDTH_COLLAPSED}px;
  border-top: 1px solid ${PALETTE_LIGHT.SURFACE_BORDER};
  display: flex;
  align-items: center;
  flex-shrink: 0;
  overflow: hidden;
`;

export const FooterAvatarArea = styled("div")`
  width: 53px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const FooterInfo = styled("div", {
  shouldForwardProp: (prop) => prop !== "isExpanded",
})<{ isExpanded: boolean }>(
  ({ isExpanded }) => `
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  padding-right: ${SPACING.regular};

  @media (min-width: ${BREAKPOINTS.tablet}px) {
    opacity: ${isExpanded ? 1 : 0};
    transition: opacity ${NAV_TRANSITION};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`,
);

const marqueeScroll = `
  white-space: nowrap;
  overflow: hidden;

  & > span {
    display: inline-block;
    transition: transform 1.5s ease;
  }

  &:hover > span {
    transform: translateX(var(--scroll-distance, 0px));
  }
`;

export const FooterName = styled("div")`
  font-size: ${FONT.BODY_REGULAR.fontSize};
  color: ${PALETTE_LIGHT.TEXT_EMPHASIS_HIGH};
  line-height: ${FONT.BODY_REGULAR.lineHeight};
  ${marqueeScroll}
`;

export const FooterAccount = styled("div")`
  font-size: ${FONT.BODY_SMALL.fontSize};
  color: ${PALETTE_LIGHT.TEXT_EMPHASIS_HIGH};
  ${marqueeScroll}
`;

export const MobileBarContainer = styled("div")`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: ${MOBILE_BAR_HEIGHT}px;
  z-index: 110;
  background: ${PALETTE_LIGHT.SURFACE_BASE};
  border-bottom: 1px solid ${PALETTE_LIGHT.SURFACE_BORDER};
  display: flex;
  align-items: center;
  padding: 0 ${SPACING.small};

  ${mediaMinWidth.tablet(`
    display: none;
  `)}
`;

export const HamburgerButton = styled("button")`
  background: none;
  border: none;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${PALETTE_LIGHT.TEXT_EMPHASIS_HIGH};
  border-radius: 4px;

  &:hover {
    background: ${PALETTE_LIGHT.SURFACE_TEXTURE};
  }
`;

export const PanelSidebarButton = styled("button")`
  background: ${PALETTE_LIGHT.SURFACE_TEXTURE};
  border: 1px solid ${PALETTE_LIGHT.SURFACE_BORDER};
  cursor: pointer;
  height: 32px;
  padding: 0 ${SPACING.regular};
  gap: ${SPACING.small};
  display: flex;
  align-items: center;
  color: ${PALETTE_LIGHT.TEXT_EMPHASIS_HIGH};
  border-radius: 4px;
  margin-left: auto;
  font-size: ${FONT.BODY_SMALL.fontSize};
  white-space: nowrap;

  &:hover {
    background: ${PALETTE_LIGHT.SURFACE_BORDER};
  }
`;

export const Backdrop = styled("div")`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(2px);
  z-index: 45;

  ${mediaMinWidth.tablet(`
    display: none;
  `)}
`;

export const StyledOverlay = styled("div", {
  shouldForwardProp: (prop) => prop !== "isExpanded",
})<{ isExpanded: boolean }>(
  ({ isExpanded }) => `
  width: ${isExpanded ? NAV_WIDTH_EXPANDED : NAV_WIDTH_COLLAPSED}px;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 5;
  position: absolute;
  transition: width ${NAV_TRANSITION};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`,
);
